<template>
  <div class="gs-daily">
    <div class="gs-daily__scroll">
      <table class="gs-daily__table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :data-column="column.key"
              :class="['gs-daily__head', { 'gs-daily__head--numeric': column.numeric }]"
              scope="col"
              :aria-sort="sortStateFor(column.key)"
            >
              <button type="button" class="gs-daily__sort" @click="sortBy(column.key)">
                <span>{{ column.label }}</span>
                <gs-icon
                  v-if="orderBy === column.key"
                  :name="ascending ? 'chevron-up' : 'chevron-down'"
                  size="14"
                  class="gs-daily__sort-icon"
                />
              </button>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!sortedRows.length" class="gs-daily__state-row">
            <td :colspan="columns.length" class="gs-daily__state">No activity in this range</td>
          </tr>
          <template v-else>
            <tr v-for="row in sortedRows" :key="row.label" class="gs-daily__row">
              <td data-cell="label" class="gs-daily__cell">{{ row.label }}</td>
              <td data-cell="plays" class="gs-daily__cell gs-daily__cell--numeric">{{ number(row.plays) }}</td>
              <td data-cell="visitors" class="gs-daily__cell gs-daily__cell--numeric">{{ number(row.visitors) }}</td>
              <td data-cell="finishes" class="gs-daily__cell gs-daily__cell--numeric">{{ number(row.finishes) }}</td>
              <td data-cell="watches" class="gs-daily__cell gs-daily__cell--numeric">{{ formatDuration(row.watches) }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { formatDuration } from '@/utils/duration';

export default {
  name: 'DailyBreakdownTable',
  props: {
    rows: { type: Array, default: () => [] },
  },
  data() {
    return {
      orderBy: '',
      ascending: false,
    };
  },
  computed: {
    columns() {
      return [
        { key: 'label', label: 'Date', numeric: false },
        { key: 'plays', label: 'Plays', numeric: true },
        { key: 'visitors', label: 'Viewer counts', numeric: true },
        { key: 'finishes', label: 'Finishes', numeric: true },
        { key: 'watches', label: 'Watch time', numeric: true },
      ];
    },
    sortedRows() {
      if (!this.orderBy) return this.rows;
      const key = this.orderBy;
      const dir = this.ascending ? 1 : -1;
      return this.rows.slice().sort((a, b) => {
        if (key === 'label') return String(a.key || a.label).localeCompare(String(b.key || b.label)) * dir;
        return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * dir;
      });
    },
  },
  methods: {
    sortBy(column) {
      if (this.orderBy === column) {
        this.ascending = !this.ascending;
      } else {
        this.orderBy = column;
        this.ascending = false;
      }
    },
    sortStateFor(column) {
      if (this.orderBy !== column) return 'none';
      return this.ascending ? 'ascending' : 'descending';
    },
    number(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString('en-US') : '0';
    },
    formatDuration,
  },
};
</script>

<style scoped>
.gs-daily {
  display: flex;
  flex-direction: column;
}

.gs-daily__scroll {
  overflow-x: auto;
}

.gs-daily__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.gs-daily__head {
  padding: 0;
  color: var(--gs-muted-foreground);
  text-align: left;
  border-bottom: 1px solid var(--gs-border);
  white-space: nowrap;
}

.gs-daily__head--numeric {
  text-align: right;
}

.gs-daily__sort {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-daily__head--numeric .gs-daily__sort {
  justify-content: flex-end;
}

.gs-daily__sort:hover {
  color: var(--gs-foreground);
}

.gs-daily__sort:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-daily__sort-icon {
  flex: none;
  color: var(--gs-primary);
}

.gs-daily__cell {
  padding: 12px 14px;
  color: var(--gs-foreground);
  border-bottom: 1px solid var(--gs-border);
  white-space: nowrap;
}

.gs-daily__cell--numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.gs-daily__state {
  padding: 32px 14px;
  color: var(--gs-muted-foreground);
  text-align: center;
}
</style>
